import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MuiTooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { IBudget, IBudgetProposals, IAllProposalDetails, IStory, IStoryContent } from '@opengov/stories-shared/types';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';

import { IEditorMessages, IModalMessages, IStoryViewMessages } from '../../../../../types';
import { MergeTagsOperation } from '../../../../common/context/MergeTagsContext';
import { MAX_PROPOSALS_IN_STORY } from '../../../../constants/proposals';
import { useUpdateAllStoryContentsMergeTagsDB } from '../../hooks';
import { NoProposalView } from '../EmptyProposal/NoProposalView';
import { RemoveBudgetConfirmationModal } from '../RemoveBudgetConfirmationModal';
import { RemoveProposalConfirmationModal } from '../RemoveProposalConfirmationModal';
import { ProposalsContentView } from './ContentView/ProposalsContentView';
import { AddProposalModal } from '../../../../../modals/components/AddProposalModal/AddProposalModal';
import { ReplaceBudgetModal } from '../../../../../modals/components/ReplaceBudgetOrProposal/ReplaceBudgetModal/ReplaceBudgetModal';
import { ReplaceProposalModal } from '../../../../../modals/components/ReplaceBudgetOrProposal/ReplaceProposalModal/ReplaceProposalModal';

import { styles } from './ProposalsView.styles';

export type IProposalsViewProps = {
  messages: IStoryViewMessages;
  modalMessages: IModalMessages;
  editorMessages: IEditorMessages;
  isTemplate: boolean;
  story: IStory;
  storyContents: IStoryContent[];
  publicationId?: string;
  userId: number;
  selectedBudget: IBudget | null;
  entitySubdomain: string;
  entityId: number;
  proposalFieldsDetails: IAllProposalDetails;
  isLoadingProposals: boolean;
}

export const ProposalsView: React.FC<IProposalsViewProps> = (props) => {
  const {
    story,
    entitySubdomain,
    entityId,
    selectedBudget,
    messages,
    modalMessages,
    editorMessages,
    proposalFieldsDetails,
    isLoadingProposals,
    isTemplate,
    storyContents,
    publicationId,
    userId
  } = props;
  const queryClient = useQueryClient();
  const { mutateAsync: doUpdateMergeTags } = useUpdateAllStoryContentsMergeTagsDB();
  const hasAddProposalReachedLimit = selectedBudget?.proposals?.length === MAX_PROPOSALS_IN_STORY;
  const isRemovingLastProposal = selectedBudget?.proposals?.length === 1;
  const isBudgetRemoved = selectedBudget && !selectedBudget.budgetTitle && !selectedBudget.proposals?.length;
  const [removeBudget, setRremoveBudget] = useState<boolean>(false);
  const [proposalToRemove, setProposalToRemove] = useState<IBudgetProposals | null>(null);
  const [isAddProposalOpen, setIsAddProposalOpen] = useState<boolean>(false);
  const [isReplaceBudgetOpen, setIsReplaceBudgetOpen] = useState<boolean>(false);
  const [replacingProposal, setReplacingProposal] = useState<IBudgetProposals | null>(null);
  const [budgetMenuAnchor, setBudgetMenuAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      {isLoadingProposals ? (
        <Box component="output" aria-label={messages.loadingProposals} sx={styles.loadingSpinner}>
          <CircularProgress aria-hidden={true} />
          <Typography variant="body2" aria-hidden={true}>{messages.loadingProposals}</Typography>
        </Box>
      ) : null}

      {!isLoadingProposals && !selectedBudget ? (
        <NoProposalView
          title={messages.noProposalsContent}
          description={messages.addProposalsContent}
          showButton={true}
          buttonTitle={messages.addProposals}
          buttonVariant="outlined"
          buttonSize="small"
          buttonAction={() => setIsAddProposalOpen(true)}
        />
      ) : null}

      {!isLoadingProposals && selectedBudget && isBudgetRemoved ? (
        <Box data-test="budget-removed-container" sx={styles.budgetRemoved}>
          <Box sx={styles.budgetDeleted}>
            <Typography variant="body2">
              {messages.budgetDeleted}
            </Typography>
            <WarningAmberIcon sx={styles.statusWarning} aria-hidden={true} />
          </Box>
          <Box sx={styles.buttonOptions}>
            <Button
              variant="outlined"
              size="small"
              data-test="add-more-option"
              onClick={() => setIsReplaceBudgetOpen(true)}
            >
              {messages.add}
            </Button>
            <Button
              variant="outlined"
              size="small"
              data-test="remove-budget"
              onClick={() => setRremoveBudget(true)}
            >
              {messages.remove}
            </Button>
          </Box>
        </Box>
      ) : null}

      {!isLoadingProposals && selectedBudget && !isBudgetRemoved ? (
        <Box sx={styles.container}>
          <Typography variant="body2">
            {messages.budget}
          </Typography>
          <Divider aria-hidden={true} />
          <Box sx={styles.nameContainer}>
            <Typography sx={styles.budgetName}>{selectedBudget?.budgetTitle}</Typography>
            <IconButton
              data-test="budget-remove-dropdown-button"
              size="small"
              sx={styles.moreButton}
              aria-label="Budget options"
              aria-haspopup="true"
              aria-controls="budget-options-menu"
              onClick={(e) => setBudgetMenuAnchor(e.currentTarget)}
            >
              <MoreVertIcon aria-hidden={true} />
            </IconButton>
            <Menu
              id="budget-options-menu"
              anchorEl={budgetMenuAnchor}
              open={Boolean(budgetMenuAnchor)}
              onClose={() => setBudgetMenuAnchor(null)}
            >
              <MenuItem
                data-test="remove-budget-button"
                onClick={() => {
                  setBudgetMenuAnchor(null);
                  setRremoveBudget(true);
                }}
              >
                <ListItemIcon>
                  <DeleteIcon fontSize="small" aria-hidden={true} />
                </ListItemIcon>
                <ListItemText>{messages.remove}</ListItemText>
              </MenuItem>
              <MenuItem
                data-test="replace-budget-button"
                onClick={() => {
                  setBudgetMenuAnchor(null);
                  setIsReplaceBudgetOpen(true)
                }}
              >
                <ListItemIcon>
                  <RefreshIcon fontSize="small" aria-hidden={true} />
                </ListItemIcon>
                <ListItemText>{messages.replace}</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
          <Box sx={styles.nameContainer}>
            <Typography variant="body2" sx={styles.proposalName}>
              {messages.proposals}
            </Typography>
            <MuiTooltip
              title={messages.proposalReachedLimit}
              placement="top"
              disableHoverListener={!hasAddProposalReachedLimit}
            >
              <span>
                <Button
                  disabled={hasAddProposalReachedLimit}
                  size="small"
                  variant="outlined"
                  data-test="add-more-option"
                  startIcon={<AddIcon />}
                  onClick={() => setIsAddProposalOpen(true)}
                >
                  {messages.addMore}
                </Button>
              </span>
            </MuiTooltip>
          </Box>
          {proposalFieldsDetails &&
            selectedBudget?.proposals?.map((proposal) => {
              return (
                <section key={proposal.id}>
                  <ProposalsContentView
                    messages={messages}
                    editorMessages={editorMessages}
                    budgetId={selectedBudget?.budgetId}
                    storyId={story._id}
                    proposal={proposal}
                    entitySubdomain={entitySubdomain}
                    proposalFieldsDetail={proposalFieldsDetails[proposal.proposalId]}
                    onReplaceProposalClick={setReplacingProposal}
                    onRemoveProposalClick={setProposalToRemove}
                  />
                </section>
              );
            })}
        </Box>
      ) : null}

      {removeBudget ? (
        <RemoveBudgetConfirmationModal
          messages={messages}
          storyId={story._id}
          setRemoveBudget={setRremoveBudget}
          onBudgetRemoved={() => {
            return doUpdateMergeTags({
              isTemplate,
              story,
              storyContents,
              publicationId: publicationId ?? '',
              userId,
              storyMergeTagsOperation: { operation: MergeTagsOperation.Remove }
            }).then(() => {
              void queryClient.invalidateQueries({ queryKey: ['storyView', 'proposals'] });
            });
          }}
        />
      ) : null}

      {proposalToRemove ? (
        <RemoveProposalConfirmationModal
          isRemoveLastProposal={isRemovingLastProposal}
          messages={messages}
          storyId={story._id}
          entityId={entityId}
          proposalId={proposalToRemove.proposalId}
          setProposalToRemove={setProposalToRemove}
          onProposalRemoved={({ mergeTags }) => {
            return doUpdateMergeTags({
              isTemplate,
              story,
              storyContents,
              publicationId: publicationId ?? '',
              userId,
              storyMergeTagsOperation: { operation: MergeTagsOperation.Update, mergeTags }
            }).then(() => {
              void queryClient.invalidateQueries({ queryKey: ['storyView', 'proposals'] });
            });
          }}
        />
      ) : null}

      {isAddProposalOpen ? (
        <AddProposalModal
          messages={modalMessages}
          selectedStoryId={story._id}
          budget={selectedBudget}
          isTemplate={isTemplate}
          story={story}
          publicationId={publicationId}
          userId={userId}
          storyContents={storyContents}
          entityId={entityId}
          onClose={() => setIsAddProposalOpen(false)}
        />
      ) : null}

      {isReplaceBudgetOpen ? (
        <ReplaceBudgetModal
          messages={modalMessages}
          selectedStoryId={story._id}
          budget={selectedBudget}
          isTemplate={isTemplate}
          story={story}
          publicationId={publicationId}
          userId={userId}
          storyContents={storyContents}
          proposalFieldsDetails={proposalFieldsDetails}
          entityId={entityId}
          onClose={() => setIsReplaceBudgetOpen(false)}
        />
      ) : null}

      {replacingProposal ? (
        <ReplaceProposalModal
          messages={modalMessages}
          selectedStoryId={story._id}
          budget={selectedBudget}
          replacingProposal={replacingProposal}
          isTemplate={isTemplate}
          story={story}
          publicationId={publicationId}
          userId={userId}
          storyContents={storyContents}
          entityId={entityId}
          onClose={() => setReplacingProposal(null)}
        />
      ) : null}
    </>
  );
};
